import React, { useState } from 'react';
import { ChevronDown, FileText, Mic, MicOff, Music, Search, X } from 'lucide-react';
import analyticsService from '../services/analytics';
import SongUsagePanel from './SongUsagePanel';

/**
 * Songs view, one job at a time: your song library, finding songs online, and
 * the AI song finder that listens (speak the title or sing a few lines).
 */
const SongsView = ({
  activeView,
  addAiSongToLibrary,
  addDebugLog,
  addOnlineSongToLibrary,
  addToSongLibrary,
  aiSingLyrics,
  aiSongListening,
  aiSongMode,
  aiSongResults,
  aiSongSearching,
  aiSongTranscript,
  clearAiSongResults,
  clearOnlineSearchResults,
  clearSongLibrary,
  clearSongUsage,
  displaySong,
  dm,
  groqEnabled,
  isSearchingOnline,
  logActivity,
  newSongCcli,
  newSongLyrics,
  newSongTitle,
  onlineSearchQuery,
  onlineSearchResults,
  removeFromSongLibrary,
  searchSongsOnline,
  setAiSongMode,
  setCurrentLyricLine,
  setDisplayContent,
  setNewSongCcli,
  setNewSongLyrics,
  setNewSongTitle,
  setOnlineSearchQuery,
  setShowAddSongForm,
  setSongSearch,
  showAddSongForm,
  songLibrary,
  songSearch,
  songUsage,
  t,
  toggleAiSongListening,
}) => {
  const [tab, setTab] = useState('mine'); // 'mine' | 'online' | 'listen'
  const [showUsage, setShowUsage] = useState(false);
  const [openLines, setOpenLines] = useState(null); // index of the song whose lines are expanded

  const tabBtn = (active) => `flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${active
    ? 'bg-brand-600 text-slate-50 shadow'
    : (dm ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}`;
  const quiet = `px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 ${dm ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`;
  const dangerQuiet = 'px-3 py-1.5 border border-red-500/40 bg-red-500/10 text-red-500 rounded-lg text-xs font-semibold hover:bg-red-600 hover:text-white transition-all';
  const input = `w-full p-3 text-sm rounded-lg border focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none ${t.inputBg}`;
  const primary = 'px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-slate-50 rounded-lg text-sm hover:from-green-500 hover:to-emerald-500 font-bold shadow-lg transition-all';

  const shownSongs = songLibrary
    .map((song, index) => ({ song, index }))
    .filter(({ song }) =>
      songSearch.trim() === '' ||
      song.title.toLowerCase().includes(songSearch.toLowerCase()) ||
      song.lyrics.toLowerCase().includes(songSearch.toLowerCase()));

  return (
    <div className={`${activeView === 'songs' ? '' : 'hidden'} lg:col-span-2 space-y-4`}>
      <div className={`rounded-2xl p-5 border shadow-lg ${dm ? 'bg-gradient-to-br from-green-900/40 to-emerald-900/40 border-green-500/30' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4 gap-2">
          <h2 className={`text-lg font-bold flex items-center gap-2 ${dm ? 'text-emerald-300' : 'text-emerald-700'}`}>
            <div className="p-1.5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
              <Music className="w-4 h-4 text-slate-50" />
            </div>
            Songs
          </h2>
          <button
            onClick={() => setShowUsage(true)}
            title="How often each song was shown, for CCLI reporting"
            className={quiet}
          >
            <FileText className="w-3.5 h-3.5" /> Usage report
          </button>
        </div>

        <div className="flex gap-2 mb-4" role="tablist" aria-label="Songs">
          <button type="button" role="tab" aria-selected={tab === 'mine'} className={tabBtn(tab === 'mine')} onClick={() => setTab('mine')}>
            My songs{songLibrary.length > 0 ? ` (${songLibrary.length})` : ''}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'online'} className={tabBtn(tab === 'online')} onClick={() => setTab('online')}>
            Find online
          </button>
          <button type="button" role="tab" aria-selected={tab === 'listen'} className={tabBtn(tab === 'listen')} onClick={() => setTab('listen')}>
            Listen (AI)
          </button>
        </div>

        {/* ---------- My songs ---------- */}
        {tab === 'mine' && (
          <div className={`p-4 rounded-xl border ${t.innerCardBg}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${dm ? 'text-emerald-300' : 'text-emerald-600'}`}>
                <Music className="w-4 h-4" />
                Song Library
              </h3>
              <div className="flex gap-1.5">
                {songLibrary.length > 0 && (
                  <button onClick={clearSongLibrary} className={dangerQuiet}>Clear</button>
                )}
                <button
                  onClick={() => setShowAddSongForm(!showAddSongForm)}
                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-slate-50 rounded-lg text-xs font-semibold hover:from-blue-500 hover:to-blue-600 shadow-lg transition-all"
                >
                  {showAddSongForm ? 'Cancel' : '+ Add Song'}
                </button>
              </div>
            </div>

            {showAddSongForm && (
              <div className={`mb-3 p-3 rounded-lg border space-y-2 ${t.innerCardBg}`}>
                <input type="text" value={newSongTitle} onChange={(e) => setNewSongTitle(e.target.value)} placeholder="Song Title" className={input} />
                <input type="text" value={newSongCcli} onChange={(e) => setNewSongCcli(e.target.value)} placeholder="CCLI song number (optional)" inputMode="numeric" className={input} />
                <textarea value={newSongLyrics} onChange={(e) => setNewSongLyrics(e.target.value)} placeholder="Song Lyrics (Enter full lyrics here)" rows="5" className={`${input} resize-none`} />
                <button onClick={addToSongLibrary} className={`w-full ${primary}`}>Add Song</button>
              </div>
            )}

            {songLibrary.length > 0 && (
              <>
                <input type="text" value={songSearch} onChange={(e) => setSongSearch(e.target.value)} placeholder="Search songs..." className={`${input} mb-3`} />
                <p className={`text-xs mb-2 ${t.textSecondary}`}>Click a song to stage it.</p>
                <div className="space-y-2">
                  {shownSongs.map(({ song, index }) => {
                    const lines = song.lyrics.split('\n').filter(line => line.trim() !== '');
                    const expanded = openLines === index;
                    return (
                      <div key={index} className={`rounded-lg border hover:border-green-400/50 transition-all overflow-hidden ${t.rowBg}`}>
                        <div className="flex items-stretch">
                          <button
                            onClick={() => displaySong(song)}
                            className="flex-1 min-w-0 p-3 hover:bg-green-500/10 text-left transition-all"
                          >
                            <div className={`text-sm font-semibold truncate ${t.textPrimary}`}>{song.title}</div>
                            <div className={`text-xs mt-0.5 truncate ${t.textSecondary}`}>{lines[0] || ''}</div>
                          </button>
                          <button
                            onClick={() => setOpenLines(expanded ? null : index)}
                            aria-expanded={expanded}
                            title="Choose a line to show"
                            className={`px-3 text-xs font-semibold flex items-center gap-1 transition-all ${t.textSecondary} hover:bg-slate-500/20`}
                          >
                            Lines <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                          </button>
                          <button
                            onClick={() => { removeFromSongLibrary(index); setOpenLines(null); }}
                            title="Remove" aria-label="Remove"
                            className="px-3 text-slate-400 hover:bg-red-600 hover:text-white transition-all"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {expanded && (
                          <div className={`border-t p-2 space-y-1 ${t.divider} ${dm ? 'bg-slate-950/30' : 'bg-gray-50'}`}>
                            <div className={`text-xs mb-1 font-semibold ${t.textMuted}`}>Show a single line live:</div>
                            {lines.map((line, lineIdx) => (
                              <button
                                key={lineIdx}
                                onClick={() => {
                                  setDisplayContent({ type: 'song', title: song.title, content: song.lyrics });
                                  setCurrentLyricLine(lineIdx);
                                  addDebugLog(`📺 Sent live: ${song.title} - Line ${lineIdx + 1}`);
                                  analyticsService.trackEvent('song_line_displayed', { title: song.title, lineNumber: lineIdx + 1 });
                                  logActivity('song_line_displayed', { title: song.title, lineNumber: lineIdx + 1, lineText: line.substring(0, 50) });
                                }}
                                className={`w-full text-left px-3 py-1.5 text-sm rounded border hover:border-emerald-500/50 transition-all ${dm ? 'text-slate-200 bg-slate-900/50 border-slate-700/50 hover:bg-emerald-600/20' : 'text-gray-700 bg-white border-gray-200 hover:bg-emerald-50'}`}
                              >
                                <span className={`font-mono mr-2 ${t.textMuted}`}>{lineIdx + 1}.</span>
                                {line}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {shownSongs.length === 0 && (
                    <p className={`text-sm text-center py-4 ${t.textSecondary}`}>No song matches “{songSearch}”.</p>
                  )}
                </div>
              </>
            )}

            {songLibrary.length === 0 && !showAddSongForm && (
              <div className={`text-center py-8 ${t.textSecondary}`}>
                <Music className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No songs yet.</p>
                <p className="text-xs mt-1">Click “+ Add Song”, or use Find online and Listen (AI) to bring songs in.</p>
              </div>
            )}
          </div>
        )}

        {/* ---------- Find online ---------- */}
        {tab === 'online' && (
          <div className={`p-4 rounded-xl border ${t.innerCardBg}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-1.5 mb-3 ${dm ? 'text-cyan-300' : 'text-cyan-700'}`}>
              <Search className="w-4 h-4" />
              Search songs online
            </h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={onlineSearchQuery}
                onChange={(e) => setOnlineSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchSongsOnline()}
                placeholder="Search songs online..."
                className={`flex-1 ${input}`}
              />
              <button
                onClick={searchSongsOnline}
                disabled={isSearchingOnline}
                className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-slate-50 rounded-lg text-sm hover:from-cyan-500 hover:to-blue-500 font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearchingOnline ? '...' : '🔍'}
              </button>
            </div>

            {onlineSearchResults.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${t.textPrimary}`}>Found {onlineSearchResults.length} song(s)</span>
                  <button onClick={clearOnlineSearchResults} className={dangerQuiet}>Clear</button>
                </div>
                <div className="space-y-2">
                  {onlineSearchResults.map((song, idx) => (
                    <div key={idx} className={`rounded-lg p-3 border border-cyan-500/20 ${t.rowBg}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-semibold ${t.textPrimary}`}>{song.title}</div>
                          <div className={`text-xs ${t.textSecondary}`}>{song.artist}</div>
                          <div className={`text-xs line-clamp-2 mt-1 ${t.textMuted}`}>{song.lyrics}</div>
                        </div>
                        <button
                          onClick={() => addOnlineSongToLibrary(song)}
                          className="shrink-0 px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-slate-50 rounded-lg text-xs font-semibold hover:from-green-500 hover:to-emerald-500 shadow-lg transition-all"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className={`text-sm text-center py-6 ${t.textSecondary}`}>Type a title or some lyrics, then press Enter.</p>
            )}
          </div>
        )}

        {/* ---------- Listen (AI) ---------- */}
        {tab === 'listen' && (
          <div className={`p-4 rounded-xl border ${t.innerCardBg}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${dm ? 'text-cyan-300' : 'text-cyan-700'}`}>
                <Mic className="w-4 h-4" />
                AI Song Search
                {groqEnabled && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-500 rounded border border-emerald-500/30">SleekHear AI</span>}
              </h3>
              {aiSongResults.length > 0 && (
                <button onClick={clearAiSongResults} className={dangerQuiet}>Clear</button>
              )}
            </div>

            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setAiSongMode('speak')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                  aiSongMode === 'speak'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-slate-50 shadow-lg'
                    : dm ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Speak Title
              </button>
              <button
                onClick={() => setAiSongMode('sing')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                  aiSongMode === 'sing'
                    ? 'bg-gradient-to-r from-rose-700 to-rose-600 text-slate-50 shadow-lg'
                    : dm ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Sing Lyrics
              </button>
            </div>

            <button
              onClick={toggleAiSongListening}
              className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg font-bold text-sm shadow-lg transition-all mb-3 ${
                aiSongListening
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-slate-50 animate-pulse'
                  : aiSongMode === 'speak'
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-slate-50 hover:from-cyan-500 hover:to-blue-500'
                  : 'bg-gradient-to-r from-rose-700 to-rose-600 text-slate-50 hover:from-rose-600 hover:to-rose-500'
              }`}
            >
              {aiSongListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              {aiSongListening
                ? (aiSongMode === 'speak' ? 'Listening...' : 'Listening to you sing...')
                : (aiSongMode === 'speak' ? 'Speak Song Name' : 'Start Singing')}
            </button>

            {aiSongMode === 'speak' && aiSongTranscript && (
              <div className={`mb-3 p-2.5 rounded-lg border border-blue-400 ${dm ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <p className={`text-sm font-semibold ${dm ? 'text-blue-200' : 'text-blue-700'}`}>Heard: "{aiSongTranscript}"</p>
              </div>
            )}

            {aiSongMode === 'sing' && aiSingLyrics && (
              <div className={`mb-3 p-2.5 rounded-lg border ${t.innerCardBg}`}>
                <p className={`text-sm font-semibold ${t.textPrimary}`}>Captured: "{aiSingLyrics}"</p>
              </div>
            )}

            {aiSongSearching && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mx-auto mb-2"></div>
                <p className={`text-sm ${t.textSecondary}`}>Searching online...</p>
              </div>
            )}

            {!aiSongSearching && aiSongResults.length > 0 && (
              <div className="space-y-2">
                {aiSongResults.map((song, idx) => (
                  <div key={idx} className={`rounded-lg p-3 border hover:border-cyan-400/40 transition-all ${t.rowBg}`}>
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${t.textPrimary}`}>{song.title}</div>
                        <div className={`text-xs ${t.textSecondary}`}>{song.artist}</div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => displaySong(song)}
                          className="px-3 py-1.5 bg-brand-600 text-slate-50 rounded text-xs font-semibold hover:bg-brand-500 shadow-lg transition-all"
                        >
                          Stage
                        </button>
                        <button
                          onClick={() => addAiSongToLibrary(song)}
                          className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-slate-50 rounded text-xs font-semibold hover:from-green-500 hover:to-emerald-500 shadow-lg transition-all"
                        >
                          + Add
                        </button>
                      </div>
                    </div>
                    <div className={`text-xs line-clamp-3 ${t.textSecondary}`}>{song.lyrics}</div>
                  </div>
                ))}
              </div>
            )}

            {!aiSongSearching && !aiSongListening && aiSongResults.length === 0 && !aiSongTranscript && !aiSingLyrics && (
              <div className={`text-center py-6 ${t.textSecondary}`}>
                <Mic className="w-10 h-10 mx-auto mb-2 opacity-50" />
                {aiSongMode === 'speak' ? (
                  <>
                    <p className="text-sm">Click the button and say the song title</p>
                    <p className="text-xs mt-1">Try: "What A Beautiful Name" or "Oceans"</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm">Click the button and sing some lyrics</p>
                    <p className="text-xs mt-1">Sing at least 5-10 words for best results</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showUsage && (
        <SongUsagePanel usage={songUsage} onClear={clearSongUsage} onClose={() => setShowUsage(false)} dm={dm} t={t} />
      )}
    </div>
  );
};

export default SongsView;
